import { useMemo } from 'react';
import {
  mediaManagerLearnMoreClick,
  mediaManagerToastAppear,
  mediaManagerUpgradeButtonClick,
  mediaManagerExceedingQuotaPopUpOpened,
  mediaManagerExceedingQuotaPopUpClick,
} from '@wix/bi-logger-media-data/v2';
import { bi } from '../../../services/loggers/bi';
import { StorageQuotaUseCase } from './useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { IStorageQuotaData } from './useStorageQuota';
import { BIPageName } from '../../../constants/Bi';

const source: Record<StorageQuotaUseCase, string> = {
  [StorageQuotaUseCase.COPY]: 'copy',
  [StorageQuotaUseCase.UPLOAD]: 'upload',
  [StorageQuotaUseCase.SHUTTERSTOCK_PURCHASE]: 'shutterstock_purchase',
  [StorageQuotaUseCase.VIDEO_MAKER]: 'video_maker',
  [StorageQuotaUseCase.MEDIA_STUDIO]: 'media_studio',
  [StorageQuotaUseCase.ADOBE_EXPRESS]: 'adobe_express',
};

const biStatus: Record<IStorageQuotaData['status'], string> = {
  storageQuotaStatusFull: 'exceed',
  storageQuotaStatusWarning: 'about_to_exceed',
  storageQuotaStatusStandard: 'not_exceeding',
  storageQuotaStatusUnlimited: 'not_exceeding',
};
export const calculateBiStatus = (storageQuotaData: IStorageQuotaData) => {
  const status = storageQuotaData?.status;
  const isUpgradeAvailable = !!storageQuotaData?.upgradeUrl;
  return !isUpgradeAvailable && status === 'storageQuotaStatusFull' ? 'exceed_no_upgrade_option' : biStatus[status];
};

export const useStorageQuotaBI = (storageQuotaData: IStorageQuotaData | undefined) => {
  const biLogger = useMemo(() => bi.logger(), []);
  const isUpgradeAvailable = !!storageQuotaData?.upgradeUrl;
  const storageStatus = calculateBiStatus(storageQuotaData);

  return useMemo(
    () => ({
      trackUpgradeButtonClick: ({
        upgradeOrigin,
      }: {
        upgradeOrigin: 'tooltip' | 'toast' | 'storage_indicator' | 'storage_is_full_popup';
      }) => {
        biLogger.report(
          mediaManagerUpgradeButtonClick({
            upgrade_origin: upgradeOrigin,
            storage_status: storageStatus,
            pageName: BIPageName.MediaManager,
          }),
        );
      },

      trackLearnMoreClick: ({
        clickOrigin,
      }: {
        clickOrigin: 'tooltip' | 'toast' | 'storage_indicator' | 'upload_popup_toast' | 'storage_is_full_popup';
      }) => {
        biLogger.report(
          mediaManagerLearnMoreClick({
            clickOrigin,
            storage_status: storageStatus,
            type: 'storage',
            pageName: BIPageName.MediaManager,
          }),
        );
      },

      trackToastOpened: (toastName: string) => {
        biLogger.report(
          mediaManagerToastAppear({
            toastName,
            type: 'storage',
            upgrade_available: isUpgradeAvailable,
          }),
        );
      },

      trackStorageFullPopupOpened: (useCase: StorageQuotaUseCase) => {
        biLogger.report(
          mediaManagerExceedingQuotaPopUpOpened({
            source: source[useCase],
          }),
        );
      },

      trackStorageFullPopupClosed: (useCase: StorageQuotaUseCase) => {
        biLogger.report(
          mediaManagerExceedingQuotaPopUpClick({
            source: source[useCase],
          }),
        );
      },
    }),
    [biLogger, storageStatus, isUpgradeAvailable],
  );
};
